import { DEPLOYMENT_STATUS, type DeploymentStatus } from '@/shared/api/deploy/types';
import type { RunStatus } from '@/shared/ui/RunStatusTag';

/** Deploy API `status` → UI `RunStatus` (useDeploymentManager와 동일 규칙) */
export function mapDeploymentStatusToRunStatus(deploymentStatus: DeploymentStatus): RunStatus {
  switch (deploymentStatus) {
    case DEPLOYMENT_STATUS.SERVING_ACTIVE:
      return 'serving_active';
    case DEPLOYMENT_STATUS.DEPLOYMENT_IN_PROGRESS:
      return 'deployment_in_progress';
    case DEPLOYMENT_STATUS.DEPLOYMENT_SUCCEEDED:
      return 'serving_inactive';
    case DEPLOYMENT_STATUS.SERVING_IN_PROGRESS:
      return 'deployment_in_progress';
    case DEPLOYMENT_STATUS.SERVING_INACTIVE:
      return 'serving_inactive';
    case DEPLOYMENT_STATUS.DEPLOYMENT_FAILED:
    case DEPLOYMENT_STATUS.SERVING_FAILED:
      return 'deployment_failed';
    default:
      return 'execution_succeeded';
  }
}

/**
 * 마운트 시 run_id로 배포를 조회해 deploymentId·서빙 상태를 맞출지 여부.
 * 실행 단계(ready/running/failed/canceled)가 아니면 조회한다.
 */
export function shouldBootstrapDeploymentFromRunApi(status: RunStatus): boolean {
  return (
    status === 'execution_succeeded' ||
    status === 'serving_active' ||
    status === 'serving_inactive' ||
    status === 'serving_in_progress' ||
    status === 'deployment_in_progress' ||
    status === 'deployment_succeeded' ||
    status === 'deployment_failed' ||
    status === 'serving_failed'
  );
}

type DeploymentStateUpdate = {
  runStatus: RunStatus;
  step: number;
  isInProgress: boolean;
  logs: string[];
};

/**
 * deploymentStatus에 따른 UI 상태(runStatus, step, isInProgress, logs) 계산
 * @param isInitialLoad - 초기 로딩 시 true, SSE 이벤트 등 실시간 업데이트 시 false
 */
export function calculateDeploymentStateUpdate(
  deploymentStatus: DeploymentStatus,
  timestamp: string,
  isInitialLoad = false
): DeploymentStateUpdate {
  const runStatus = mapDeploymentStatusToRunStatus(deploymentStatus);
  let step = -1;
  let isInProgress = false;
  let logs: string[] = [];

  switch (deploymentStatus) {
    case DEPLOYMENT_STATUS.SERVING_ACTIVE:
      step = 3;
      if (!isInitialLoad) {
        logs = [
          `[${timestamp}] 패키징이 완료되었습니다`,
          `[${timestamp}] 배포가 완료되었습니다`,
          `[${timestamp}] 서빙을 진행하고 있습니다`,
          `[${timestamp}] ✅ 서빙이 활성화되었습니다`,
        ];
      }
      break;

    case DEPLOYMENT_STATUS.DEPLOYMENT_IN_PROGRESS:
      step = 0;
      isInProgress = true;
      if (!isInitialLoad) {
        logs = [`[${timestamp}] 패키징을 진행하고 있습니다`];
      }
      break;

    case DEPLOYMENT_STATUS.DEPLOYMENT_SUCCEEDED:
      step = 1;
      isInProgress = true;
      if (!isInitialLoad) {
        logs = [`[${timestamp}] 패키징이 완료되었습니다`, `[${timestamp}] 배포를 진행하고 있습니다`];
      }
      break;

    case DEPLOYMENT_STATUS.SERVING_IN_PROGRESS:
      step = 2;
      isInProgress = true;
      if (!isInitialLoad) {
        logs = [
          `[${timestamp}] 패키징이 완료되었습니다`,
          `[${timestamp}] 배포가 완료되었습니다`,
          `[${timestamp}] 서빙을 진행하고 있습니다`,
        ];
      }
      break;

    case DEPLOYMENT_STATUS.SERVING_INACTIVE:
      step = -1;
      break;

    case DEPLOYMENT_STATUS.DEPLOYMENT_FAILED:
      step = 0;
      break;

    case DEPLOYMENT_STATUS.SERVING_FAILED:
      step = 2;
      break;
  }

  return { runStatus, step, isInProgress, logs };
}
